import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
interface PresignedResponse {
    upload_url: string | null;
    s3_patch: string | null;
    expires_in: number | null;
    note: string | null;
    isLoading : boolean 
}

export interface iMoleculePresignedState {
    group_name: string | null;
    library_name: string | null;
    file_type: string | null;
    file_extension: string | null;
    isLoading: boolean;
    target_name : string | null;
    off_target_name : string | null;
    message: string | null;
    // 각 파일 타입별로 response 객체를 따로 관리
    response: PresignedResponse;
    drugResponse: PresignedResponse | null;
    targetResponse: PresignedResponse | null;
    offTargetResponse: PresignedResponse | null;  
    assayResponse: PresignedResponse | null;
    expressionResponse: PresignedResponse | null;
    round : number | null; // 라운드 정보 추가
    workflow_type:string|null
}

const initialResponse: PresignedResponse = {
    upload_url: null,
    s3_patch: null,
    expires_in: null,
    note: null,
    isLoading: false
};

const initialState: iMoleculePresignedState = {
    group_name: null,
    library_name: null,
    file_type: null,
    file_extension: null,
    isLoading: false,
    target_name : null,
    off_target_name : null,
    message: "",
    response: initialResponse,
    drugResponse: null,
    targetResponse: null,
    offTargetResponse: null, 
    assayResponse: null,
    expressionResponse: null,
    round: null,
    workflow_type : null
};

export const uploadMoleculePresignedAPI = createAsyncThunk(
    "MOLECULE_UPLOAD_PRESIGNED",
    async (params: { 
        group_name: string; 
        library_name: string; 
        file_extension: string;
        file_name?: string;
        molecule_type? : string;
        round?: number;
        workflow_name : string;
        target_name?: string;
        off_target_name?: string;
        workflow_type? :string;
    }, { rejectWithValue }) => {
        try {
            const djangoEndpoint = getDjangoEndpoint();
            const pathParam = '/upload/molecule-presigned/';
            
            // API 요청 데이터
            const requestData: any = {
                group_name: params.group_name,
                library_name: params.library_name,
                file_name: params.file_name || null,
                file_extension: params.file_extension,
                molecule_type : params.molecule_type,
                workflow_name : params.workflow_name,
                round: params.round,
                workflow_type : params.workflow_type

            };
            
            // molecule_type이 'target'이고 target_name이 제공된 경우 추가
            if (params.molecule_type === 'target' && params.target_name !== undefined) {
                requestData.target_name = params.target_name;
            }

            // molecule_type이 'off-target'이고 off_target_name이 제공된 경우 추가
            if (params.molecule_type === 'off-target' && params.off_target_name !== undefined) {
                requestData.off_target_name = params.off_target_name;
            }
            
            const response = await axiosInstance.post(
                `${djangoEndpoint}${pathParam}`, 
                requestData,
                {
                    withCredentials: true,
                }
            );
            
            const fileType = params.molecule_type;
            
            return {
                response: response.data,
                fileType: fileType,
                molecule_type: params.molecule_type
            };
        } catch (error) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// Molecule Upload Slice
export const uploadMoleculePresignedSlice = createSlice({
    name: "uploadMoleculePresigned",
    initialState,
    reducers: {
        clearPresignedUrls: (state) => {
            state.response = initialResponse;
            state.drugResponse = null;
            state.targetResponse = null;
            state.offTargetResponse = null; 
            state.assayResponse = null;
            state.expressionResponse = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadMoleculePresignedAPI.pending, (state) => {
                state.isLoading = true;
                state.message = "";
            })
            .addCase(uploadMoleculePresignedAPI.fulfilled, (state, action) => {
                state.isLoading = false;
                
                // 기존 response는 하위 호환성을 위해 유지
                state.response = action.payload.response;
                
                // molecule_type으로 응답 저장 위치 결정
                const { fileType, response } = action.payload;
                if (fileType === 'drug') {
                    state.drugResponse = response;
                } else if (fileType === 'target') {
                    state.targetResponse = response;
                } else if (fileType === 'off-target') {  
                    state.offTargetResponse = response;
                } else if (fileType === 'assay') {
                    state.assayResponse = response;
                } else if (fileType === 'expression') {
                    state.expressionResponse = response;
                } else {
                    console.warn('Unknown file type:', fileType);
                }
            })
            .addCase(uploadMoleculePresignedAPI.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload as string;
                console.error('Upload failed:', action.payload);
            });
    },
});

export const { clearPresignedUrls } = uploadMoleculePresignedSlice.actions;
export default uploadMoleculePresignedSlice.reducer;
